// apps/cli/src/commands/bootstrap/ui.ts
import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { Command, Option } from "clipanion";
import { DefaultAzureCredential } from "@azure/identity";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors } from "../../lib/output.js";
import { getAzAccount } from "../../lib/auth.js";
import { readBootstrapState } from "../../lib/bootstrap-state.js";
import {
  resolveFoundryEndpointWithWait,
  getSignedInUserOid,
  ensureFounderFoundryRole,
  writeWebEnvLocal,
  installWebDeps,
  runInherit,
  assertNodeVersion,
  serveOnboardingUiDetached,
  stopOnboardingUi,
  autoOpenOnboardingUi,
} from "../../lib/bootstrap-ui.js";
import { deploySimpleStacey } from "../../lib/simple-stacey.js";

export class BootstrapUiCommand extends M8tCommand {
  static paths = [["bootstrap", "ui"]];
  static usage = Command.Usage({
    description: "Deploy Simple Stacey + start the local onboarding chat UI in the background (returns immediately).",
    details: [
      "Run after `m8t bootstrap launch`, in parallel with `status --watch`. Waits for the cloud",
      "installer's foundry-create phase, grants you Foundry data-plane access, deploys a no-brain",
      "Simple Stacey (stacey-intake), writes apps/web/.env.local (chat-only), installs deps, and",
      "starts the webapp detached so a coding agent can continue without opening a separate terminal.",
      "",
      "The command returns as soon as the server is ready (or after ~45 s if it is still compiling).",
      "Use `--stop` to shut it down, `--foreground` to keep the old blocking behaviour.",
    ].join("\n"),
    examples: [
      ["Start the onboarding UI (backgrounds the server, returns)", "$0 bootstrap ui --repo-root /path/to/m8t"],
      ["Stop the running onboarding UI", "$0 bootstrap ui --stop"],
      ["Prep only (don't serve at all)", "$0 bootstrap ui --repo-root /path/to/m8t --prep-only"],
      ["Foreground (blocking, old behaviour)", "$0 bootstrap ui --repo-root /path/to/m8t --foreground"],
    ],
  });

  repoRoot = Option.String("--repo-root");
  port = Option.String("--port", "3000");
  endpoint = Option.String("--endpoint", {
    description: "Foundry project endpoint to target — disambiguates when the subscription has multiple projects.",
  });
  prepOnly = Option.Boolean("--prep-only", false);
  skipInstall = Option.Boolean("--skip-install", false);
  stop = Option.Boolean("--stop", false);
  foreground = Option.Boolean("--foreground", false);

  async executeCommand(): Promise<number> {
    // --stop is completely independent: it doesn't need bootstrap state or Foundry.
    if (this.stop === true) {
      const stopped = stopOnboardingUi();
      if (stopped) {
        this.context.stdout.write(`${colors.success("✓")} stopped the onboarding chat UI.\n`);
      } else {
        this.context.stdout.write(`  ${colors.dim("nothing to stop (no onboarding-ui.pid found).")}\n`);
      }
      return 0;
    }

    const state = await readBootstrapState();
    if (!state) {
      throw new LocalCliError({
        code: "BOOTSTRAP_NO_STATE",
        message: "No bootstrap is in flight (no ~/.m8t-stack/bootstrap.json).",
        hint: "Run 'm8t bootstrap launch --location <region>' first.",
      });
    }
    const repoRoot = (typeof this.repoRoot === "string" ? this.repoRoot : undefined) ?? process.cwd();

    if (this.port !== "3000") {
      throw new LocalCliError({
        code: "BOOTSTRAP_UI_PORT_MSAL_LOCKED",
        message: `--port ${this.port} is not supported: the app registration's redirect URI is fixed to localhost:3000, so Microsoft sign-in only works there.`,
        hint: "Omit --port (it defaults to 3000). If port 3000 is busy, stop the other process using it.",
      });
    }

    const out = (m: string) => { this.context.stderr.write(`  ${colors.dim(m)}\n`); };

    if (fs.existsSync(path.join(os.homedir(), ".m8t-stack", "config.yaml"))) {
      out(colors.error("⚠ an existing ~/.m8t-stack/config.yaml will override the onboarding app registration in apps/web — if Microsoft sign-in fails, move it aside and retry."));
    }
    const credential = new DefaultAzureCredential();
    const account = await getAzAccount();

    assertNodeVersion();

    out("waiting for Foundry (the installer's foundry-create phase)…");
    const { endpoint, accountScope } = await resolveFoundryEndpointWithWait(
      {
        credential,
        subscriptionId: state.subscriptionId,
        endpoint: typeof this.endpoint === "string" ? this.endpoint : undefined,
      },
      { onWait: out },
    );

    out("granting you Foundry data-plane access…");
    const oid = await getSignedInUserOid();
    await ensureFounderFoundryRole({ credential, subscriptionId: state.subscriptionId, principalId: oid, accountScope });

    out("deploying Simple Stacey (stacey-intake)…");
    const version = await deploySimpleStacey({ credential, endpoint, repoRoot });

    const envPath = writeWebEnvLocal({
      repoRoot, tenantId: account.tenantId, clientId: state.appRegClientId, foundryEndpoint: endpoint,
    });

    if (!this.skipInstall) {
      out("installing apps/web dependencies (pnpm)…");
      await installWebDeps(repoRoot);
    }

    this.context.stdout.write(
      `${colors.success("✓")} Simple Stacey is live (stacey-intake v${version}).\n` +
      `  env: ${envPath}\n` +
      `  Opening ${colors.field("http://localhost:3000")} in your browser → Sign in with Microsoft → chat with Stacey.\n` +
      `  ${colors.dim("(If it doesn't open, browse to http://localhost:3000 yourself.)")}\n` +
      `  ${colors.dim("First turn may say 'warming up' for a few minutes while access propagates — that's expected.")}\n`,
    );

    if (this.prepOnly === true) {
      this.context.stdout.write(`  ${colors.hint("serve it yourself:")} pnpm --filter web dev\n`);
      return 0;
    }

    if (this.foreground === true) {
      out("starting the webapp on :3000 (Ctrl-C to stop)…");
      await runInherit("pnpm", ["--filter", "web", "dev"], repoRoot, "BOOTSTRAP_UI_DEV_FAILED");
      return 0;
    }

    // Default: background (detached) serve — the command returns so the agent can continue.
    out("starting the webapp on :3000 in the background…");
    const { alreadyRunning, logPath } = await serveOnboardingUiDetached({ repoRoot, port: this.port });
    if (alreadyRunning) {
      this.context.stdout.write(
        `${colors.success("✓")} chat UI already running at http://localhost:${this.port}\n`,
      );
    } else {
      this.context.stdout.write(
        `${colors.success("✓")} chat UI is live at http://localhost:${this.port}\n` +
        `  ${colors.dim(`logs: ${logPath} · stop: m8t bootstrap ui --stop`)}\n`,
      );
    }
    autoOpenOnboardingUi(`http://localhost:${this.port}`);
    return 0;
  }
}
